import React from 'react';
import Icon from '../../../components/AppIcon';

const CorrectiveActionsModal = ({ email, correctiveActions, onClose, loading }) => {
  if (!email) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
                <Icon name="Sparkles" size={24} />
                AI-Generated Corrective Actions
              </h2>
              <p className="text-blue-100">For: {email?.recipient_email}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
              <p className="text-neutral-600">Generating corrective actions with AI...</p>
            </div>
          ) : correctiveActions ? (
            <div className="space-y-6">
              {/* Error Details */}
              <div className="bg-red-50 rounded-lg border border-red-200 p-4">
                <h3 className="font-semibold text-neutral-900 mb-2 flex items-center gap-2">
                  <Icon name="AlertCircle" size={18} />
                  Error Details
                </h3>
                <p className="text-sm text-neutral-700">
                  {email?.error_message || email?.notification_delivery_log?.[0]?.error_details || 'Unknown error'}
                </p>
                <div className="mt-2 text-xs text-neutral-600">
                  Retry Count: {email?.retry_count || 0}
                </div>
              </div>

              {/* Immediate Actions */}
              {correctiveActions?.immediateActions && correctiveActions?.immediateActions?.length > 0 && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="Zap" size={18} className="text-orange-600" />
                    Immediate Actions Required
                  </h3>
                  <ul className="space-y-2">
                    {correctiveActions?.immediateActions?.map((action, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircle" size={16} className="text-orange-600 mt-1" />
                        <span className="text-sm text-neutral-700">{action}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Technical Fixes */}
              {correctiveActions?.technicalFixes && correctiveActions?.technicalFixes?.length > 0 && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="Settings" size={18} className="text-blue-600" />
                    Technical Fixes
                  </h3>
                  <div className="space-y-4">
                    {correctiveActions?.technicalFixes?.map((fix, index) => (
                      <div key={index} className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <h4 className="font-medium text-neutral-900 mb-2">{fix?.fix}</h4>
                        <p className="text-sm text-neutral-700">{fix?.implementation}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preventive Measures */}
              {correctiveActions?.preventiveMeasures && correctiveActions?.preventiveMeasures?.length > 0 && (
                <div className="bg-white rounded-lg border border-neutral-200 p-4">
                  <h3 className="font-semibold text-neutral-900 mb-3 flex items-center gap-2">
                    <Icon name="Shield" size={18} className="text-green-600" />
                    Preventive Measures
                  </h3>
                  <ul className="space-y-2">
                    {correctiveActions?.preventiveMeasures?.map((measure, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Icon name="CheckCircle" size={16} className="text-green-600 mt-1" />
                        <span className="text-sm text-neutral-700">{measure}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Estimated Resolution Time */}
              {correctiveActions?.estimatedResolutionTime && (
                <div className="bg-purple-50 rounded-lg border border-purple-200 p-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={18} className="text-purple-600" />
                    <span className="font-semibold text-neutral-900">Estimated Resolution Time:</span>
                    <span className="text-neutral-700">{correctiveActions?.estimatedResolutionTime}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-12">
              <Icon name="AlertCircle" size={48} className="mx-auto text-neutral-300 mb-4" />
              <p className="text-neutral-600">No corrective actions available</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-neutral-50 px-6 py-4 border-t border-neutral-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-neutral-200 text-neutral-700 rounded-lg hover:bg-neutral-300 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default CorrectiveActionsModal;
